import React, { useMemo } from 'react';
import { Chip } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { getStatusRole, getStatusPalette } from '../../constants/listingStatus';

/**
 * StatusChip - the one way to show state (design.md §5.4).
 *
 * Listing status and transaction type keep their own distinct colour per value,
 * because six statuses need six colours a reader can tell apart at a glance;
 * those live in constants/listingStatus.js so they are still defined once.
 * Everything else (MLS readiness, phase, priority) resolves to a semantic theme
 * role and renders as alpha(role.main, 0.16) with role.dark text.
 *
 * Props:
 *  - value : the domain value, e.g. 'Active', 'For Sale', 'urgent'
 *  - kind  : which map to read - 'listing' (default) | 'transaction' | 'mls' | 'phase' | 'priority'
 *  - role  : override the resolved role when a caller already knows it
 *  - label : override the displayed text (defaults to `value`)
 *  - icon  : optional leading icon; pair it with the label, never colour alone
 *  - size, variant and any other Chip props pass through
 */
const StatusChip = ({
  value,
  kind = 'listing',
  role: roleProp,
  label,
  icon,
  size = 'small',
  sx,
  ...chipProps
}) => {
  const theme = useTheme();

  const tokens = useMemo(() => {
    // A value with its own named colour wins, so statuses stay distinguishable.
    const named = !roleProp && getStatusPalette(value, kind);
    if (named) return { bg: named.bgcolor, fg: named.color };

    const role = roleProp || getStatusRole(value, kind);
    if (role === 'default') {
      const { slate } = theme.palette.modern;
      return { bg: alpha(slate[500], 0.14), fg: slate[700] };
    }
    const palette = theme.palette[role] || theme.palette.primary;
    return { bg: alpha(palette.main, 0.16), fg: palette.dark || palette.main };
  }, [theme, roleProp, value, kind]);

  return (
    <Chip
      size={size}
      icon={icon}
      label={label ?? value}
      sx={{
        backgroundColor: tokens.bg,
        color: tokens.fg,
        fontWeight: 600,
        '& .MuiChip-icon': { color: 'inherit' },
        ...sx,
      }}
      {...chipProps}
    />
  );
};

export default StatusChip;
